import { useState } from "react";
import { Send } from "lucide-react";
import type { AskQuestion } from "../chatClient";

/** 把一组问答整理成发回给模型的文本。模型看到的是"问题 → 回答"的配对，
 *  和它在提示词里被要求接收的散文式回答等价。 */
function formatAnswers(questions: AskQuestion[], answers: Record<string, string[]>, freeText: Record<string, string>): string {
  return questions
    .map((q) => {
      const picked = answers[q.id] ?? [];
      const extra = (freeText[q.id] ?? "").trim();
      const parts = [...picked];
      if (extra) parts.push(extra);
      return `${q.question}\n答：${parts.length ? parts.join("；") : "（未作答，按你的推荐值处理）"}`;
    })
    .join("\n\n");
}

/** 模型提问轮的可交互表单：一个问题一块，可选项点选、并可自由填写。 */
export function QuestionForm({
  questions,
  disabled,
  onSubmit,
}: {
  questions: AskQuestion[];
  disabled: boolean;
  onSubmit: (text: string) => void;
}) {
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [freeText, setFreeText] = useState<Record<string, string>>({});

  function toggle(q: AskQuestion, label: string) {
    setPicked((prev) => {
      const cur = prev[q.id] ?? [];
      if (q.multiSelect) {
        return { ...prev, [q.id]: cur.includes(label) ? cur.filter((l) => l !== label) : [...cur, label] };
      }
      return { ...prev, [q.id]: cur.includes(label) ? [] : [label] };
    });
  }

  const answeredCount = questions.filter(
    (q) => (picked[q.id]?.length ?? 0) > 0 || (freeText[q.id] ?? "").trim()
  ).length;

  return (
    <form
      className="ask-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (disabled) return;
        onSubmit(formatAnswers(questions, picked, freeText));
      }}
    >
      {questions.map((q, i) => (
        <div className="ask-question" key={q.id}>
          <div className="ask-question-head">
            <span className="ask-question-index">{i + 1}</span>
            {q.header && <span className="ask-question-header">{q.header}</span>}
          </div>
          <p className="ask-question-text">{q.question}</p>
          {q.options && q.options.length > 0 && (
            <ul className="ask-options">
              {q.options.map((o) => {
                const active = (picked[q.id] ?? []).includes(o.label);
                return (
                  <li key={o.label}>
                    <button
                      type="button"
                      className={`ask-option ${active ? "active" : ""}`}
                      disabled={disabled}
                      onClick={() => toggle(q, o.label)}
                    >
                      <span className="ask-option-label">{o.label}</span>
                      {o.description && <span className="ask-option-desc">{o.description}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {q.allowFreeText !== false && (
            <input
              className="form-input ask-free-input"
              placeholder={q.options?.length ? "或自行填写…" : "请填写…"}
              disabled={disabled}
              value={freeText[q.id] ?? ""}
              onChange={(e) => setFreeText((prev) => ({ ...prev, [q.id]: e.target.value }))}
            />
          )}
        </div>
      ))}
      <div className="ask-actions">
        <span className="ask-progress">
          已回答 {answeredCount} / {questions.length}
        </span>
        <button className="btn btn-text" type="button" disabled={disabled} onClick={() => onSubmit("全部推荐")}>
          全部按推荐值
        </button>
        <button className="btn btn-clay" type="submit" disabled={disabled || answeredCount === 0}>
          <Send size={14} className="icon" />
          <span>提交回答</span>
        </button>
      </div>
    </form>
  );
}
